import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LayoutDashboard, Shirt, CalendarDays, Settings, Instagram, Upload, Image, Truck, Type, FolderTree, ToggleLeft, ToggleRight, Loader2, LogOut, Percent, Sparkles, TrendingUp, Trash2, Edit3, Check, X, Eye, Tag } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import AdminSidebar from "../components/admin/AdminSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFeatureToggles() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toggles, setToggles] = useState({
    complete_the_look_enabled: true,
    bundle_discount_percent: 5,
    bundle_discount_type: "total",
    discount_tier_2: 10,
    discount_tier_3: 15,
    discount_tier_4plus: 20,
    upsell_popup_enabled: true,
    recently_viewed_enabled: true,
    frequently_bundled_enabled: true,
  });
  const [pairs, setPairs] = useState([]);
  const [pairsLoading, setPairsLoading] = useState(false);
  const [editingPair, setEditingPair] = useState(null);
  const [editCount, setEditCount] = useState(0);
  const [qvConfig, setQvConfig] = useState({
    enabled: true,
    dispatch_note: "Your outfit will be dispatched 1–2 days before your selected date.",
    available_text: "Available for your selected dates.",
    unavailable_text: "Unavailable for your selected dates.",
    cta_text: "Book Now",
    change_dates_text: "Change Dates",
    select_date_prompt: "Select your event date to check availability",
    highlights_label: "Highlights",
  });
  const [qvSaving, setQvSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("adminToken");
    if (!token) { navigate("/admin/login"); return; }
    fetchToggles();
    fetchPairs();
    fetchQvConfig();
  }, []);

  const fetchToggles = async () => {
    try {
      const res = await axios.get(`${API}/settings/feature-toggles`);
      setToggles(res.data.toggles);
    } catch { toast.error("Error loading settings"); }
    finally { setLoading(false); }
  };

  const fetchPairs = async () => {
    setPairsLoading(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.get(`${API}/bundle-analytics`, { headers: { Authorization: `Bearer ${token}` } });
      setPairs(res.data.pairs || []);
    } catch {}
    finally { setPairsLoading(false); }
  };

  const fetchQvConfig = async () => {
    try {
      const res = await axios.get(`${API}/settings/quickview`);
      setQvConfig(res.data.config);
    } catch {}
  };

  const handleSaveQv = async () => {
    setQvSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.put(`${API}/settings/quickview`, { config: qvConfig }, { headers: { Authorization: `Bearer ${token}` } });
      setQvConfig(res.data.config);
      toast.success("Quick View settings saved");
    } catch { toast.error("Error saving Quick View settings"); }
    finally { setQvSaving(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("adminToken");
      const res = await axios.put(`${API}/settings/feature-toggles`, { toggles }, { headers: { Authorization: `Bearer ${token}` } });
      setToggles(res.data.toggles);
      toast.success("Settings saved");
    } catch { toast.error("Error saving settings"); }
    finally { setSaving(false); }
  };

  const handleDeletePair = async (pairKey) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.delete(`${API}/bundle-analytics/${pairKey}`, { headers: { Authorization: `Bearer ${token}` } });
      setPairs(prev => prev.filter(p => p.pair_key !== pairKey));
      toast.success("Pair deleted");
    } catch { toast.error("Error deleting pair"); }
  };

  const handleSavePairCount = async (pairKey) => {
    try {
      const token = localStorage.getItem("adminToken");
      await axios.put(`${API}/bundle-analytics/${pairKey}`, { count: editCount }, { headers: { Authorization: `Bearer ${token}` } });
      setPairs(prev => prev.map(p => p.pair_key === pairKey ? { ...p, count: editCount } : p));
      setEditingPair(null);
      toast.success("Count updated");
    } catch { toast.error("Error updating count"); }
  };

  const toggle = (key) => setToggles(p => ({ ...p, [key]: !p[key] }));
  const handleLogout = () => { localStorage.removeItem("adminToken"); navigate("/admin/login"); };

  if (loading) return <div className="min-h-screen bg-[#FAF8F0] flex items-center justify-center"><Loader2 className="w-10 h-10 animate-spin text-[#9A7B4F]" /></div>;

  const features = [
    {
      key: "complete_the_look_enabled",
      title: "Complete the Look",
      desc: "Show complementary products from other categories on the product page. Includes cross-category bundle pricing with discount.",
    },
    {
      key: "upsell_popup_enabled",
      title: "Smart Upsell Popup",
      desc: "Show a popup suggesting complementary items when a customer proceeds to booking.",
    },
    {
      key: "recently_viewed_enabled",
      title: "Recently Viewed Items",
      desc: "Display a section of recently viewed items on the product page (up to 6 items).",
    },
    {
      key: "frequently_bundled_enabled",
      title: "Frequently Bundled Together",
      desc: "Show items most commonly bundled by other customers. Uses real booking analytics to auto-suggest top combinations.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <AdminSidebar />

      <main className="lg:ml-64 p-6 md:p-10">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h1 className="text-3xl text-[#676664]" data-testid="feature-toggles-title">Feature Toggles</h1>
            <p className="text-[#676664]/40 text-sm font-light">Control storefront features</p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[11px] tracking-[0.15em] uppercase" data-testid="save-toggles-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save Changes
          </Button>
        </div>

        <div className="max-w-2xl space-y-6">
          {/* Tiered Discount Settings */}
          <div className="bg-white p-6 border border-[#282828]/10" data-testid="tiered-discount-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-[#F0D9D5]/30 flex items-center justify-center"><Percent className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} /></div>
              <div>
                <h3 className="text-sm text-[#282828] font-medium">Multi-Item Discount Tiers</h3>
                <p className="text-[11px] text-[#676664]/50 font-light">Discounts on rental fees when customers add multiple items. Applied to all items in cart.</p>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { key: "discount_tier_2", label: "2 items", desc: "Discount when 2 items are in cart" },
                { key: "discount_tier_3", label: "3 items", desc: "Discount when 3 items are in cart" },
                { key: "discount_tier_4plus", label: "4+ items", desc: "Discount when 4 or more items are in cart" },
              ].map(tier => (
                <div key={tier.key} className="flex items-center gap-4 p-3 bg-[#FAF8F0] border border-[#282828]/5">
                  <div className="flex-1 min-w-0">
                    <span className="text-[12px] text-[#282828] font-medium">{tier.label}</span>
                    <span className="text-[10px] text-[#676664]/50 ml-2">{tier.desc}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Input
                      type="number" min={0} max={50}
                      value={toggles[tier.key]}
                      onChange={(e) => setToggles(p => ({ ...p, [tier.key]: Math.max(0, Math.min(50, Number(e.target.value))) }))}
                      className="w-20 border-[#282828]/15 text-center"
                      data-testid={`input-${tier.key}`}
                    />
                    <span className="text-[11px] text-[#676664]">%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feature Toggles */}
          {(features || []).map((f) => (
            <div key={f.key} className="bg-white p-6 border border-[#282828]/10">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#F0D9D5]/30 flex items-center justify-center flex-shrink-0">
                    {f.key === "frequently_bundled_enabled" ? (
                      <TrendingUp className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
                    ) : (
                      <Sparkles className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
                    )}
                  </div>
                  <div>
                    <h3 className="text-sm text-[#282828] font-medium">{f.title}</h3>
                    <p className="text-[11px] text-[#676664]/50 font-light mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
                <button
                  onClick={() => toggle(f.key)}
                  className="flex-shrink-0 mt-1"
                  data-testid={`toggle-${f.key}`}
                >
                  {toggles[f.key] ? (
                    <ToggleRight className="w-10 h-10 text-[#9A7B4F]" />
                  ) : (
                    <ToggleLeft className="w-10 h-10 text-[#676664]/30" />
                  )}
                </button>
              </div>
              <div className="mt-3 ml-13">
                <span className={`text-[10px] tracking-[0.1em] uppercase font-medium ${toggles[f.key] ? "text-[#9A7B4F]" : "text-[#676664]/30"}`}>
                  {toggles[f.key] ? "Enabled" : "Disabled"}
                </span>
              </div>
            </div>
          ))}

          {/* Bundle Analytics Section */}
          <div className="bg-white p-6 border border-[#282828]/10 mt-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F0D9D5]/30 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm text-[#282828] font-medium">Bundle Analytics</h3>
                  <p className="text-[11px] text-[#676664]/50 font-light">Items customers most frequently bundle together</p>
                </div>
              </div>
              <Button onClick={fetchPairs} variant="outline" className="text-[10px] tracking-[0.1em] uppercase border-[#282828]/15 text-[#676664]" data-testid="refresh-analytics-btn">
                Refresh
              </Button>
            </div>

            {pairsLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#9A7B4F]" /></div>
            ) : pairs.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-[13px] text-[#676664]/40 font-light">No bundle data yet. Analytics will appear once customers start bundling items.</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="analytics-pairs-list">
                {(pairs || []).map((pair) => (
                  <div key={pair.pair_key} className="flex items-center gap-3 p-3 border border-[#282828]/8 hover:border-[#282828]/15 transition-colors" data-testid={`pair-${pair.pair_key}`}>
                    {/* Item images */}
                    <div className="flex -space-x-2 flex-shrink-0">
                      <div className="w-10 h-12 overflow-hidden bg-[#f7f5f4] border-2 border-white relative z-10">
                        {pair.image_1 && <img src={pair.image_1} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="w-10 h-12 overflow-hidden bg-[#f7f5f4] border-2 border-white">
                        {pair.image_2 && <img src={pair.image_2} alt="" className="w-full h-full object-cover" />}
                      </div>
                    </div>
                    {/* Names */}
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-[#282828] truncate">{pair.name_1 || pair.outfit_id_1}</p>
                      <p className="text-[10px] text-[#676664]/40">+</p>
                      <p className="text-[11px] text-[#282828] truncate">{pair.name_2 || pair.outfit_id_2}</p>
                    </div>
                    {/* Count */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {editingPair === pair.pair_key ? (
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            min={0}
                            value={editCount}
                            onChange={(e) => setEditCount(Math.max(0, Number(e.target.value)))}
                            className="w-16 h-7 text-xs border-[#282828]/15"
                            data-testid={`edit-count-${pair.pair_key}`}
                          />
                          <button onClick={() => handleSavePairCount(pair.pair_key)} className="p-1 text-[#9A7B4F] hover:text-[#8B6B3F]" data-testid={`save-count-${pair.pair_key}`}>
                            <Check className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => setEditingPair(null)} className="p-1 text-[#676664]/40 hover:text-[#676664]">
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <span className="bg-[#9A7B4F]/10 text-[#9A7B4F] text-[10px] font-medium px-2 py-0.5 min-w-[40px] text-center" data-testid={`count-${pair.pair_key}`}>
                            {pair.count}x
                          </span>
                          <button
                            onClick={() => { setEditingPair(pair.pair_key); setEditCount(pair.count); }}
                            className="p-1 text-[#676664]/30 hover:text-[#9A7B4F]"
                            data-testid={`edit-btn-${pair.pair_key}`}
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDeletePair(pair.pair_key)}
                        className="p-1 text-[#676664]/30 hover:text-red-400"
                        data-testid={`delete-btn-${pair.pair_key}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick View Configuration Section */}
          <div className="bg-white p-6 border border-[#282828]/10 mt-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#F0D9D5]/30 flex items-center justify-center">
                  <Eye className="w-5 h-5 text-[#9A7B4F]" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="text-sm text-[#282828] font-medium">Quick View Settings</h3>
                  <p className="text-[11px] text-[#676664]/50 font-light">Edit all Quick View text and behaviour</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setQvConfig(p => ({ ...p, enabled: !p.enabled }))}
                  data-testid="toggle-quickview-enabled"
                >
                  {qvConfig.enabled ? (
                    <ToggleRight className="w-8 h-8 text-[#9A7B4F]" />
                  ) : (
                    <ToggleLeft className="w-8 h-8 text-[#676664]/30" />
                  )}
                </button>
                <Button
                  onClick={handleSaveQv}
                  disabled={qvSaving}
                  className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white text-[10px] tracking-[0.1em] uppercase"
                  data-testid="save-quickview-btn"
                >
                  {qvSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save"}
                </Button>
              </div>
            </div>
            <div className="space-y-4">
              {[
                { key: "cta_text", label: "CTA Button Text" },
                { key: "available_text", label: "Available Message" },
                { key: "unavailable_text", label: "Unavailable Message" },
                { key: "change_dates_text", label: "Change Dates Link Text" },
                { key: "select_date_prompt", label: "Select Date Prompt" },
                { key: "highlights_label", label: "Highlights Section Label" },
                { key: "dispatch_note", label: "Dispatch Note" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-[10px] tracking-[0.1em] uppercase text-[#676664]/50 mb-1.5 block">{label}</label>
                  <Input
                    value={qvConfig[key] || ""}
                    onChange={(e) => setQvConfig(p => ({ ...p, [key]: e.target.value }))}
                    className="border-[#282828]/15 text-[13px]"
                    data-testid={`qv-${key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
